
import './index.css';

import React, { useEffect, useState } from 'react';

import StatusBadge from '../../ui/StatusBadge';
import RecordSection from '../RecordSection';
import SubRecordModal from '../../ui/SubRecordModal';




import Factory from '../../../records/modules';

const MSF_STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'achieved', label: 'Achieved' },
  { value: 'at_risk', label: 'At Risk' },
  { value: 'missed', label: 'Missed' },
];

const EMPTY_MSF = {
  name: '',
  description: '',
  status: 'not_started',
  unit: '',
  baselineValue: '',
  targetValue: '',
  currentValue: '',
  dueDate: '',
};

function SuccessFactors({ meta, domain, parentId }) {

  const [successFactors, setSuccessFactors] = useState(domain.successFactors || []);
  useEffect(() => {
    setSuccessFactors(domain.successFactors || []);
  }, [domain.successFactors]);

  const [msfModal, setMsfModal] = useState({ open: false, editing: null, form: EMPTY_MSF, submitting: false, error: '' });

  function openAddMsf() {
    setMsfModal({ open: true, editing: null, form: EMPTY_MSF, submitting: false, error: '' });
  }

  function openEditMsf(factor) {
    setMsfModal({
      open: true,
      editing: factor,
      form: {
        name: factor.name || '',
        description: factor.description || '',
        status: factor.status || 'not_started',
        unit: factor.unit || '',
        baselineValue: factor.baselineValue ?? '',
        targetValue: factor.targetValue ?? '',
        currentValue: factor.currentValue ?? '',
        dueDate: factor.dueDate || '',
      },
      submitting: false,
      error: '',
    });
  }

  function closeMsfModal() {
    setMsfModal((s) => ({ ...s, open: false }));
  }

  function setMsfField(field, value) {
    setMsfModal((s) => ({ ...s, form: { ...s.form, [field]: value } }));
  }

  async function submitMsf() {
    setMsfModal((s) => ({ ...s, submitting: true, error: '' }));
    try {
      const payload = {
        name: msfModal.form.name.trim(),
        description: msfModal.form.description.trim() || null,
        status: msfModal.form.status,
        unit: msfModal.form.unit.trim() || null,
        baselineValue: msfModal.form.baselineValue !== '' ? Number(msfModal.form.baselineValue) : null,
        targetValue: msfModal.form.targetValue !== '' ? Number(msfModal.form.targetValue) : null,
        currentValue: msfModal.form.currentValue !== '' ? Number(msfModal.form.currentValue) : null,
        dueDate: msfModal.form.dueDate || null,
      };
      if (msfModal.editing) {
        const updated = await Factory({ slug: meta.slug }).Data.updateRecord(msfModal.editing.id, payload);
        setSuccessFactors((current) => current.map((factor) => (
          factor.data.id === updated.id ? { data: updated } : factor
        )));
      } else {
        const created = await Factory({ slug: meta.slug }).Data.createRecord({
          ...payload,
          [meta.parentId]: parentId,
        });
        setSuccessFactors((current) => [...current, { data: created }]);
      }
      closeMsfModal();
    } catch (err) {
      const message = err?.response?.data?.message || 'Unable to save success factor.';
      setMsfModal((s) => ({ ...s, submitting: false, error: message }));
    }
  }

  async function handleDeleteMsf(factor) {
    if (!window.confirm(`Delete success factor "${factor.name}"? This cannot be undone.`)) return;
    try {
      await Factory({ slug: meta.slug }).Data.deleteRecord(factor.id);
      setSuccessFactors((current) => current.filter((item) => item.data.id !== factor.id));
    } catch (err) {
      alert(err?.response?.data?.message || 'Unable to delete success factor.');
    }
  }

  return (
    <>
      <RecordSection
        title="Measurable Success Factors"
        className="record-success-factors"
        actions={(
          <button className="button-secondary" onClick={openAddMsf} type="button">
            + Add Success Factor
          </button>
        )}
      >
        {successFactors.length === 0 ? (
          <p>No success factors added yet.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Factor</th>
                <th>Status</th>
                <th>Baseline</th>
                <th>Current</th>
                <th>Target</th>
                <th>Unit</th>
                <th>Due</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {successFactors.map((factor) => (
                <tr key={factor.data.id}>
                  <td>{factor.data.name}</td>
                  <td><StatusBadge value={factor.data.status} /></td>
                  <td>{factor.data.baselineValue ?? '-'}</td>
                  <td>{factor.data.currentValue ?? '-'}</td>
                  <td>{factor.data.targetValue ?? '-'}</td>
                  <td>{factor.data.unit || '-'}</td>
                  <td>{factor.data.dueDate || '-'}</td>
                  <td>
                    <div className="sub-record-row-actions">
                      <button className="button-row-edit" onClick={() => openEditMsf(factor.data)} type="button">Edit</button>
                      <button className="button-row-delete" onClick={() => handleDeleteMsf(factor.data)} type="button">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </RecordSection>


      {/* MSF Modal */}
      <SubRecordModal
        error={msfModal.error}
        isOpen={msfModal.open}
        isSubmitting={msfModal.submitting}
        onClose={closeMsfModal}
        onSubmit={submitMsf}
        submitLabel={msfModal.editing ? 'Save Changes' : 'Add Success Factor'}
        title={msfModal.editing ? 'Edit Success Factor' : 'Add Measurable Success Factor'}
      >
        <div className="form-grid">
          <label>
            Name <span aria-hidden="true">*</span>
            <input
              autoFocus
              onChange={(e) => setMsfField('name', e.target.value)}
              placeholder="e.g. Customer satisfaction score"
              required
              type="text"
              value={msfModal.form.name}
            />
          </label>
          <label>
            Description
            <textarea
              onChange={(e) => setMsfField('description', e.target.value)}
              placeholder="What does this factor measure?"
              value={msfModal.form.description}
            />
          </label>
          <div className="form-grid two-col">
            <label>
              Status
              <select onChange={(e) => setMsfField('status', e.target.value)} value={msfModal.form.status}>
                {MSF_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
            <label>
              Unit
              <input
                onChange={(e) => setMsfField('unit', e.target.value)}
                placeholder="e.g. %, points, $"
                type="text"
                value={msfModal.form.unit}
              />
            </label>
          </div>
          <div className="form-grid two-col">
            <label>
              Baseline Value
              <input
                onChange={(e) => setMsfField('baselineValue', e.target.value)}
                placeholder="Starting value"
                step="any"
                type="number"
                value={msfModal.form.baselineValue}
              />
            </label>
            <label>
              Target Value
              <input
                onChange={(e) => setMsfField('targetValue', e.target.value)}
                placeholder="Goal value"
                step="any"
                type="number"
                value={msfModal.form.targetValue}
              />
            </label>
          </div>
          <div className="form-grid two-col">
            <label>
              Current Value
              <input
                onChange={(e) => setMsfField('currentValue', e.target.value)}
                placeholder="Latest measurement"
                step="any"
                type="number"
                value={msfModal.form.currentValue}
              />
            </label>
            <label>
              Due Date
              <input
                onChange={(e) => setMsfField('dueDate', e.target.value)}
                type="date"
                value={msfModal.form.dueDate}
              />
            </label>
          </div>
        </div>
      </SubRecordModal>
    </>
  );
}

export default SuccessFactors;